// The runs list down the left of Claims → Runs: grouped by month with each
// month's accrued total, a mark on every run that needs a look, a search that
// also finds a run by client, Medicaid ID or ICN, and a filter.

import { format, parseISO } from 'date-fns';
import { destinationLabel } from '../../../lib/serviceModel';
import { fmt$ } from './format';

/** "11 – 17 Oct", or "27 Sep – 3 Oct" across a month end. */
export const weekLabel = (run) => {
  if (!run?.date_from) return '—';
  const a = parseISO(run.date_from); const b = parseISO(run.date_to || run.date_from);
  if (run.date_from === run.date_to) return format(a, 'd MMM');
  return format(a, 'MMM') === format(b, 'MMM') ? `${format(a, 'd')} – ${format(b, 'd MMM')}` : `${format(a, 'd MMM')} – ${format(b, 'd MMM')}`;
};

/** What the marks say needs a person. */
export const needsALook = (m) => m?.state === 'done' && (m.serious > 0 || m.changed > 0 || m.newVisits > 0);

const Mark = ({ tone, children }) => (
  <span className={`run-mark run-mark-${tone}`}>{children}</span>
);

function RunMarks({ run, mark }) {
  const items = [];
  if (!mark) items.push(<span key="n" className="run-mark-none">not checked yet</span>);
  else if (mark.state === 'checking') items.push(<span key="c" className="run-mark-none">checking…</span>);
  else if (mark.state === 'error') items.push(<Mark key="e" tone="review">could not check</Mark>);
  else {
    if (mark.serious) items.push(<Mark key="s" tone="serious">{mark.serious} serious</Mark>);
    if (mark.review) items.push(<Mark key="r" tone="review">{mark.review} to review</Mark>);
    if (mark.changed) items.push(<Mark key="ch" tone="review">{mark.changed} changed in AxisCare</Mark>);
    if (mark.newVisits) items.push(<Mark key="nv" tone="review">{mark.newVisits} new in AxisCare</Mark>);
  }
  if (run.status === 'closed') items.push(<Mark key="p" tone="paid">paid</Mark>);
  return <span className="run-marks">{items}</span>;
}

/**
 * @param {object[]} p.runs       the runs to show, already searched and filtered, newest first
 * @param {number}   p.total      how many runs exist before filtering
 */
export default function RunList({
  runs, total, marks, selectedId, onSelect, search, onSearch, searching, filter, onFilter, onNew, onImport,
}) {
  const groups = [];
  for (const run of runs) {
    const key = run.date_from ? format(parseISO(run.date_from), 'MMMM yyyy') : 'No date';
    let g = groups[groups.length - 1];
    if (!g || g.key !== key) { g = { key, runs: [], accrued: 0 }; groups.push(g); }
    g.runs.push(run); g.accrued += Number(run.total_accrued_amount) || 0;
  }
  const lookCount = Object.values(marks).filter(needsALook).length;

  return (
    <aside className="runs-list" aria-label="Billing runs">
      <div className="runs-list-head">
        <div className="runs-list-title">
          <h3>Runs <span>{total}</span></h3>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-outline btn-sm" onClick={onImport} title="Import historical RAs from Drive">Import</button>
            <button className="btn btn-primary btn-sm" onClick={onNew}>+ New run</button>
          </div>
        </div>
        <label className="sr-only" htmlFor="runs-search">Find a run</label>
        <input id="runs-search" type="search" className="form-input" placeholder="Week, client, Medicaid ID or ICN"
          value={search} onChange={e => onSearch(e.target.value)} />
        <div className="runs-filters" role="group" aria-label="Show">
          {[['all', 'All'], ['look', `Needs a look${lookCount ? ` ${lookCount}` : ''}`], ['paid', 'Paid']].map(([k, label]) => (
            <button key={k} className={`runs-filter${filter === k ? ' on' : ''}`} aria-pressed={filter === k} onClick={() => onFilter(k)}>{label}</button>
          ))}
        </div>
        {searching && <div className="runs-list-note">Searching lines…</div>}
      </div>

      <div className="runs-list-body">
        {runs.length === 0 && (
          <div className="runs-list-note" style={{ padding: 16 }}>
            {filter === 'look' ? 'No checked run needs a look. Runs not checked yet are left out.' : 'No run matches.'}
          </div>
        )}
        {groups.map(g => (
          <section key={g.key} aria-label={g.key}>
            <div className="runs-month"><span>{g.key}</span><span>{fmt$(g.accrued)}</span></div>
            {g.runs.map(run => (
              <button key={run.id} className={`run-card${run.id === selectedId ? ' on' : ''}`}
                aria-current={run.id === selectedId ? 'true' : undefined} onClick={() => onSelect(run)}>
                <span className="run-card-top">
                  <b>{weekLabel(run)}</b>
                  <span className="run-card-amt">{fmt$(run.total_accrued_amount)}</span>
                </span>
                <span className="run-card-sub">
                  {run.total_lines ?? 0} lines · {destinationLabel(run.payer)}
                  {run.status === 'submitted' ? ' · filed' : ''}
                </span>
                <RunMarks run={run} mark={marks[run.id]} />
              </button>
            ))}
          </section>
        ))}
      </div>
      <div className="runs-list-keys">
        <kbd>↑</kbd> <kbd>↓</kbd> move between runs · <kbd>F</kbd> full width · <kbd>Esc</kbd> back
      </div>
    </aside>
  );
}
